import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { crmApi, formatAED } from "@/lib/crm";
import { useSettings, stageBadgeClass } from "@/contexts/SettingsContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Plus, CheckCircle2, Circle, MessageSquare, Activity } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] tracking-overline uppercase text-neutral-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-sm outline-none focus:border-neutral-900 dark:focus:border-white transition";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const stages = settings.stages || [];
  const services = settings.services || [];
  const sources = settings.sources || [];
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [taskForm, setTaskForm] = useState({ title: "", due_date: "", description: "" });

  const load = async () => {
    const d = await crmApi.getLead(id);
    setData(d);
    setForm(d.lead);
  };

  useEffect(() => {
    load();
    crmApi.listUsers().then(setUsers);
  }, [id]);

  if (!data) return <div className="p-8 text-xs tracking-overline uppercase text-neutral-500">Loading lead…</div>;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onNumChange = (k) => (e) => setForm({ ...form, [k]: e.target.value === "" ? null : parseFloat(e.target.value) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        nationality: form.nationality,
        service: form.service,
        source: form.source,
        status: form.status,
        assigned_to: form.assigned_to || null,
        message: form.message,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        proposal_value: form.proposal_value ?? null,
        expected_revenue: form.expected_revenue ?? null,
        actual_revenue: form.actual_revenue ?? null,
      };
      await crmApi.updateLead(id, payload);
      toast.success("Lead updated");
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    await crmApi.deleteLead(id);
    toast.success("Lead deleted");
    navigate("/crm/leads");
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    await crmApi.addNote(id, noteBody.trim());
    setNoteBody("");
    await load();
  };

  const addTask = async () => {
    if (!taskForm.title.trim()) return;
    await crmApi.addTask(id, {
      title: taskForm.title.trim(),
      description: taskForm.description,
      due_date: taskForm.due_date || null,
      assigned_to: user?.user_id,
    });
    setTaskForm({ title: "", due_date: "", description: "" });
    await load();
  };

  const toggleTask = async (t) => {
    await crmApi.updateTask(t.id, { completed: !t.completed });
    await load();
  };

  const removeTask = async (t) => {
    await crmApi.deleteTask(t.id);
    await load();
  };

  return (
    <div className="p-6 md:p-10 space-y-8">
      <div className="flex items-center justify-between gap-4">
        <Link to="/crm/leads" data-testid="back-to-leads" className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition">
          <ArrowLeft size={14}/> All leads
        </Link>
        <div className="flex gap-2">
          <button onClick={remove} data-testid="lead-delete" className="inline-flex items-center gap-2 px-4 py-2 border border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-[11px] tracking-overline uppercase hover:bg-rose-50 dark:hover:bg-rose-950/30 transition"><Trash2 size={14}/> Delete</button>
          <button onClick={save} disabled={saving} data-testid="lead-save" className="inline-flex items-center gap-2 px-5 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase hover:opacity-90 transition disabled:opacity-60"><Save size={14}/> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div>
        <div className={`inline-block text-[10px] tracking-overline uppercase px-2 py-1 border ${stageBadgeClass(stages.find(s => s.label === form.status)?.color)}`}>{form.status}</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl">{data.lead.full_name}</h1>
        <div className="mt-2 text-sm text-neutral-500">{data.lead.email} · {data.lead.phone}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Contact</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name"><input data-testid="lead-fullname" className={inputCls} value={form.full_name || ""} onChange={onChange("full_name")} /></Field>
              <Field label="Email"><input data-testid="lead-email" className={inputCls} type="email" value={form.email || ""} onChange={onChange("email")} /></Field>
              <Field label="Phone"><input data-testid="lead-phone" className={inputCls} value={form.phone || ""} onChange={onChange("phone")} /></Field>
              <Field label="Nationality"><input data-testid="lead-nationality" className={inputCls} value={form.nationality || ""} onChange={onChange("nationality")} /></Field>
            </div>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Enquiry & Pipeline</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Service">
                <select data-testid="lead-service" className={inputCls} value={form.service || ""} onChange={onChange("service")}>
                  {services.map((s) => <option key={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="lead-status" className={inputCls} value={form.status || ""} onChange={onChange("status")}>
                  {stages.map((s) => <option key={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Source">
                <input data-testid="lead-source" list="lead-source-list" className={inputCls} value={form.source || ""} onChange={onChange("source")} />
                <datalist id="lead-source-list">
                  {sources.map((s) => <option key={s.id} value={s.label} />)}
                </datalist>
              </Field>
              <Field label="Assigned to">
                <select data-testid="lead-assigned" className={inputCls} value={form.assigned_to || ""} onChange={onChange("assigned_to")}>
                  <option value="">— Unassigned —</option>
                  {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
                </select>
              </Field>
              <Field label="Start date"><input data-testid="lead-startdate" type="date" className={inputCls} value={form.start_date?.slice(0, 10) || ""} onChange={onChange("start_date")} /></Field>
              <Field label="End date"><input data-testid="lead-enddate" type="date" className={inputCls} value={form.end_date?.slice(0, 10) || ""} onChange={onChange("end_date")} /></Field>
            </div>
            <Field label="Enquiry message">
              <textarea data-testid="lead-message" className={inputCls + " resize-y min-h-[100px]"} value={form.message || ""} onChange={onChange("message")} />
            </Field>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950 space-y-4">
            <div className="text-[11px] tracking-overline uppercase text-neutral-500">Revenue (AED)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Proposal value"><input data-testid="lead-proposal" type="number" className={inputCls + " tabular-nums"} value={form.proposal_value ?? ""} onChange={onNumChange("proposal_value")} /></Field>
              <Field label="Expected revenue"><input data-testid="lead-expected" type="number" className={inputCls + " tabular-nums"} value={form.expected_revenue ?? ""} onChange={onNumChange("expected_revenue")} /></Field>
              <Field label="Actual revenue"><input data-testid="lead-actual" type="number" className={inputCls + " tabular-nums"} value={form.actual_revenue ?? ""} onChange={onNumChange("actual_revenue")} /></Field>
            </div>
          </section>

          {/* Notes */}
          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
            <div className="flex items-center gap-2 text-[11px] tracking-overline uppercase text-neutral-500 mb-4"><MessageSquare size={12}/> Notes</div>
            <div className="flex gap-2">
              <textarea data-testid="note-body" className={inputCls + " min-h-[70px]"} placeholder="Add a note…" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} />
              <button data-testid="note-add" onClick={addNote} className="px-4 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase self-start">Add</button>
            </div>
            <ul className="mt-5 space-y-3">
              {data.notes.length === 0 ? <li className="text-sm text-neutral-500">No notes yet.</li> :
                data.notes.map((n) => (
                  <li key={n.id} className="border-l-2 border-neutral-900 dark:border-white pl-3 py-1" data-testid={`note-${n.id}`}>
                    <div className="text-sm whitespace-pre-wrap">{n.body}</div>
                    <div className="text-[11px] text-neutral-500 mt-1">{n.author_name} · {format(new Date(n.created_at), "d MMM yyyy · HH:mm")}</div>
                  </li>
                ))}
            </ul>
          </section>
        </div>

        {/* Right: tasks + activity */}
        <div className="space-y-8">
          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
            <div className="flex items-center gap-2 text-[11px] tracking-overline uppercase text-neutral-500 mb-4"><CheckCircle2 size={12}/> Tasks</div>
            <div className="space-y-2 mb-4">
              <input data-testid="task-title" className={inputCls} placeholder="Task title…" value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} />
              <input data-testid="task-due" type="date" className={inputCls} value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
              <button data-testid="task-add" onClick={addTask} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-neutral-900 text-white dark:bg-white dark:text-black text-[11px] tracking-overline uppercase"><Plus size={12}/> Add task</button>
            </div>
            <ul className="space-y-2">
              {data.tasks.length === 0 ? <li className="text-sm text-neutral-500">No tasks.</li> :
                data.tasks.map((t) => (
                  <li key={t.id} className="flex items-start gap-2 p-2 border border-neutral-200 dark:border-neutral-800" data-testid={`task-${t.id}`}>
                    <button onClick={() => toggleTask(t)} data-testid={`task-toggle-${t.id}`} className="mt-0.5">
                      {t.completed ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-neutral-400" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${t.completed ? "line-through text-neutral-400" : ""}`}>{t.title}</div>
                      {t.due_date && <div className="text-[11px] text-neutral-500">Due {format(new Date(t.due_date), "d MMM yyyy")}</div>}
                    </div>
                    <button onClick={() => removeTask(t)} className="text-neutral-400 hover:text-rose-500"><Trash2 size={14}/></button>
                  </li>
                ))}
            </ul>
          </section>

          <section className="border border-neutral-200 dark:border-neutral-800 p-6 bg-white dark:bg-neutral-950">
            <div className="flex items-center gap-2 text-[11px] tracking-overline uppercase text-neutral-500 mb-4"><Activity size={12}/> Activity</div>
            {data.activities.length === 0 ? <div className="text-sm text-neutral-500">No activity yet.</div> :
              <ol className="relative border-l border-neutral-200 dark:border-neutral-800 pl-5 space-y-4">
                {data.activities.map((a) => (
                  <li key={a.id} className="relative">
                    <div className="absolute -left-[27px] top-1 h-2 w-2 rounded-full bg-neutral-900 dark:bg-white" />
                    <div className="text-sm">{a.description}</div>
                    <div className="text-[11px] text-neutral-500">{a.actor_name} · {format(new Date(a.created_at), "d MMM · HH:mm")}</div>
                  </li>
                ))}
              </ol>}
          </section>
        </div>
      </div>
    </div>
  );
}
